/**
 * useAssemblies Hook
 *
 * Manages assembly data with Supabase backend integration.
 * Handles CRUD operations and real-time data synchronization.
 */

import { useState, useEffect, useCallback } from 'react';
import type { Assembly, AssemblyComponent, DbAssembly } from '../types';
import { supabase } from '../supabaseClient';
import { logger } from '../lib/logger';
import { useTeam } from '../contexts/TeamContext';

/**
 * `assembly_components` rows as fetched with the joined `components(*)` shape.
 * The component join is a SQL `select('*')` whose row type isn't known to
 * TypeScript: PostgREST returns numerics as strings, and the join may be a
 * row, array, or null depending on the relation. Type tightening here cascades
 * dozens of errors through downstream string/number coercions, so we keep it
 * permissive at the boundary and let the `parseFloat || 0` defenses handle
 * anything weird the DB returns.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DbAssemblyComponentJoined = any;

/**
 * Pulls a human-readable message off whatever shape Supabase or PostgREST hand
 * back. PostgREST errors don't extend Error — they're plain objects with a
 * `message` field.
 */
function extractErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message || fallback;
  if (
    err &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
  ) {
    return (err as { message: string }).message;
  }
  return fallback;
}

interface UseAssembliesReturn {
  assemblies: Assembly[];
  loading: boolean;
  error: string | null;
  addAssembly: (
    name: string,
    components: Array<{ componentId: string; quantity: number }>,
    description?: string,
    notes?: string
  ) => Promise<void>;
  updateAssembly: (
    id: string,
    updates: {
      name?: string;
      description?: string;
      notes?: string;
      components?: Array<{ componentId: string; quantity: number }>;
    }
  ) => Promise<void>;
  deleteAssembly: (id: string) => Promise<void>;
  checkComponentUsage: (componentId: string) => Promise<{
    isUsed: boolean;
    assemblies: Array<{ id: string; name: string }>;
  }>;
  markAssembliesIncomplete: (assemblyIds: string[]) => Promise<void>;
  refreshAssemblies: () => Promise<void>;
}

export function useAssemblies(): UseAssembliesReturn {
  const { currentTeam } = useTeam();
  const [assemblies, setAssemblies] = useState<Assembly[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch all assemblies with their components
   */
  const fetchAssemblies = useCallback(async () => {
    if (!currentTeam) {
      setAssemblies([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch assemblies
      const { data: assembliesData, error: assembliesError } = await supabase
        .from('assemblies')
        .select('*')
        .eq('team_id', currentTeam.id)
        .order('created_at', { ascending: false });

      if (assembliesError) throw assembliesError;

      if (!assembliesData || assembliesData.length === 0) {
        setAssemblies([]);
        return;
      }

      // Fetch assembly components with component details
      const { data: componentsData, error: componentsError } = await supabase
        .from('assembly_components')
        .select(
          `
          *,
          component:components(*)
        `
        )
        .in(
          'assembly_id',
          assembliesData.map(a => a.id)
        )
        .order('sort_order', { ascending: true });

      if (componentsError) throw componentsError;

      // Map to Assembly[] format
      const mappedAssemblies: Assembly[] = assembliesData.map(
        (dbAssembly: DbAssembly) => {
          const assemblyComponents = (
            (componentsData || []) as DbAssemblyComponentJoined[]
          )
            .filter(ac => ac.assembly_id === dbAssembly.id)
            .map(ac => {
              const assemblyComp: AssemblyComponent = {
                id: ac.id,
                assemblyId: ac.assembly_id,
                componentId: ac.component_id,
                componentName: ac.component_name,
                componentManufacturer: ac.component_manufacturer,
                componentPartNumber: ac.component_part_number,
                quantity: parseFloat(ac.quantity),
                sortOrder: ac.sort_order || 0,
              };

              // Add component data if exists
              if (ac.component) {
                assemblyComp.component = {
                  id: ac.component.id,
                  name: ac.component.name,
                  description: ac.component.description || '',
                  category: ac.component.category || 'Other',
                  componentType: ac.component.component_type || 'hardware',
                  laborSubtype: ac.component.labor_subtype,
                  productType: ac.component.category || 'Other',
                  manufacturer: ac.component.manufacturer || '',
                  manufacturerPN: ac.component.manufacturer_part_number || '',
                  supplier: ac.component.supplier || '',
                  unitCostNIS: parseFloat(ac.component.unit_cost_ils || 0),
                  unitCostUSD: parseFloat(ac.component.unit_cost_usd || 0),
                  unitCostEUR: parseFloat(ac.component.unit_cost_eur || 0),
                  currency: ac.component.currency || 'NIS',
                  // Always derive originalCost from the currency-specific field.
                  // original_cost in DB is stored as unit_cost_usd for all currencies.
                  originalCost: parseFloat(
                    ac.component.currency === 'EUR'
                      ? ac.component.unit_cost_eur ||
                          ac.component.unit_cost_ils ||
                          0
                      : ac.component.currency === 'USD'
                        ? ac.component.unit_cost_usd ||
                          ac.component.unit_cost_ils ||
                          0
                        : ac.component.unit_cost_ils || 0
                  ),
                  quoteDate: ac.component.created_at?.split('T')[0] || '',
                  quoteFileUrl: '',
                  notes: ac.component.notes,
                  createdAt: ac.component.created_at,
                  updatedAt: ac.component.updated_at,
                };
              }

              return assemblyComp;
            });

          return {
            id: dbAssembly.id,
            name: dbAssembly.name,
            description: dbAssembly.description,
            isComplete: dbAssembly.is_complete,
            notes: dbAssembly.notes,
            components: assemblyComponents,
            createdAt: dbAssembly.created_at,
            updatedAt: dbAssembly.updated_at,
          };
        }
      );

      setAssemblies(mappedAssemblies);
    } catch (err: unknown) {
      logger.error('Error fetching assemblies:', err);
      setError(extractErrorMessage(err, 'Failed to load assemblies'));
    } finally {
      setLoading(false);
    }
  }, [currentTeam]);

  /**
   * Add a new assembly
   */
  const addAssembly = useCallback(
    async (
      name: string,
      components: Array<{ componentId: string; quantity: number }>,
      description?: string,
      notes?: string
    ) => {
      if (!currentTeam) throw new Error('No active team');

      try {
        setError(null);

        // Fetch component details for snapshots
        const { data: componentDetails, error: fetchError } = await supabase
          .from('components')
          .select('id, name, manufacturer, manufacturer_part_number')
          .in(
            'id',
            components.map(c => c.componentId)
          );

        if (fetchError) throw fetchError;

        // Create assembly
        const { data: assemblyData, error: assemblyError } = await supabase
          .from('assemblies')
          .insert({
            name,
            description,
            notes,
            is_complete: true,
            team_id: currentTeam.id,
          })
          .select()
          .single();

        if (assemblyError) throw assemblyError;

        // Create assembly components
        const assemblyComponents = components.map((comp, index) => {
          const componentDetail = componentDetails?.find(
            c => c.id === comp.componentId
          );
          return {
            assembly_id: assemblyData.id,
            component_id: comp.componentId,
            component_name: componentDetail?.name || 'Unknown',
            component_manufacturer: componentDetail?.manufacturer,
            component_part_number: componentDetail?.manufacturer_part_number,
            quantity: comp.quantity,
            sort_order: index,
            team_id: currentTeam.id,
          };
        });

        const { error: componentsError } = await supabase
          .from('assembly_components')
          .insert(assemblyComponents);

        if (componentsError) throw componentsError;

        // Refresh assemblies
        await fetchAssemblies();
      } catch (err: unknown) {
        logger.error('Error adding assembly:', err);
        setError(extractErrorMessage(err, 'Failed to add assembly'));
        throw err;
      }
    },
    [fetchAssemblies, currentTeam]
  );

  /**
   * Update an existing assembly
   */
  const updateAssembly = useCallback(
    async (
      id: string,
      updates: {
        name?: string;
        description?: string;
        notes?: string;
        components?: Array<{ componentId: string; quantity: number }>;
      }
    ) => {
      if (!currentTeam) throw new Error('No active team');

      try {
        setError(null);

        // Update assembly metadata
        const assemblyUpdates: Partial<DbAssembly> = {};
        if (updates.name !== undefined) assemblyUpdates.name = updates.name;
        if (updates.description !== undefined)
          assemblyUpdates.description = updates.description;
        if (updates.notes !== undefined) assemblyUpdates.notes = updates.notes;

        if (Object.keys(assemblyUpdates).length > 0) {
          const { error: updateError } = await supabase
            .from('assemblies')
            .update(assemblyUpdates)
            .eq('id', id)
            .eq('team_id', currentTeam.id);

          if (updateError) throw updateError;
        }

        // Update components if provided
        if (updates.components) {
          // Delete existing components
          const { error: deleteError } = await supabase
            .from('assembly_components')
            .delete()
            .eq('assembly_id', id);

          if (deleteError) throw deleteError;

          // Fetch component details for snapshots
          const { data: componentDetails, error: fetchError } = await supabase
            .from('components')
            .select('id, name, manufacturer, manufacturer_part_number')
            .in(
              'id',
              updates.components.map(c => c.componentId)
            );

          if (fetchError) throw fetchError;

          // Insert new components
          const assemblyComponents = updates.components.map((comp, index) => {
            const componentDetail = componentDetails?.find(
              c => c.id === comp.componentId
            );
            return {
              assembly_id: id,
              component_id: comp.componentId,
              component_name: componentDetail?.name || 'Unknown',
              component_manufacturer: componentDetail?.manufacturer,
              component_part_number: componentDetail?.manufacturer_part_number,
              quantity: comp.quantity,
              sort_order: index,
              team_id: currentTeam.id,
            };
          });

          const { error: insertError } = await supabase
            .from('assembly_components')
            .insert(assemblyComponents);

          if (insertError) throw insertError;

          // Check if assembly is still complete
          const isComplete = updates.components.every(c =>
            componentDetails?.some(d => d.id === c.componentId)
          );

          await supabase
            .from('assemblies')
            .update({ is_complete: isComplete })
            .eq('id', id)
            .eq('team_id', currentTeam.id);
        }

        // Refresh assemblies
        await fetchAssemblies();
      } catch (err: unknown) {
        logger.error('Error updating assembly:', err);
        setError(extractErrorMessage(err, 'Failed to update assembly'));
        throw err;
      }
    },
    [fetchAssemblies, currentTeam]
  );

  /**
   * Delete an assembly
   */
  const deleteAssembly = useCallback(
    async (id: string) => {
      if (!currentTeam) throw new Error('No active team');

      try {
        setError(null);

        // .select() so an RLS-filtered DELETE is detectable: it affects zero
        // rows and raises nothing, so without this the refetch below simply
        // shows the assembly still present with no message at all. Deletion
        // became admin-only in #117. Same guard as useQuotations.
        const { data, error: deleteError } = await supabase
          .from('assemblies')
          .delete()
          .eq('id', id)
          .eq('team_id', currentTeam.id)
          .select('id');

        if (deleteError) throw deleteError;

        if (!data || data.length === 0) {
          throw new Error(
            'Delete blocked by row-level security or row not found — deleting an assembly requires a team admin'
          );
        }

        // Refresh assemblies
        await fetchAssemblies();
      } catch (err: unknown) {
        logger.error('Error deleting assembly:', err);
        setError(extractErrorMessage(err, 'Failed to delete assembly'));
        throw err;
      }
    },
    [fetchAssemblies, currentTeam]
  );

  /**
   * Check if a component is used in any assemblies
   */
  const checkComponentUsage = useCallback(
    async (componentId: string) => {
      if (!currentTeam) return { isUsed: false, assemblies: [] };

      try {
        const { data, error } = await supabase
          .from('assembly_components')
          .select(
            `
          assembly_id,
          assembly:assemblies(id, name, team_id)
        `
          )
          .eq('component_id', componentId);

        if (error) throw error;

        // Filter by team_id. PostgREST's join shape is dynamic; narrow at
        // each step rather than asserting the whole array.
        type AssemblyJoin = {
          id: string;
          name: string;
          team_id: string;
        } | null;
        const assemblies = (data || [])
          .map(item => (item as unknown as { assembly: AssemblyJoin }).assembly)
          .filter(
            (a): a is NonNullable<AssemblyJoin> =>
              a !== null && a.team_id === currentTeam.id
          )
          .map(a => ({ id: a.id, name: a.name }));

        return {
          isUsed: assemblies.length > 0,
          assemblies,
        };
      } catch (err: unknown) {
        logger.error('Error checking component usage:', err);
        return { isUsed: false, assemblies: [] };
      }
    },
    [currentTeam]
  );

  /**
   * Flag assemblies as incomplete after a component they referenced was deleted.
   *
   * Deleting a component does NOT remove the junction row —
   * `assembly_components_component_id_fkey` is `ON DELETE SET NULL`, and the
   * `component_name` / `component_manufacturer` / `component_part_number`
   * snapshots on that row survive. So the assembly goes on listing the part by
   * name while no longer costing it, which is a worse failure than the row
   * vanishing would be, and invisible without this flag.
   *
   * Nothing else recomputes `is_complete`: `updateAssembly` only does it when the
   * caller edits the component list.
   *
   * `.select()` for the same reason as `useComponents.deleteComponent`: an
   * RLS-filtered UPDATE affects zero rows and raises nothing, so without reading
   * the affected rows back a silently-refused write looks identical to a
   * successful one.
   *
   * Does not refresh — the caller does, once, after the whole delete. This runs
   * per component inside a bulk loop, and a fetch per component is what the
   * one-dialog design exists to avoid.
   */
  const markAssembliesIncomplete = useCallback(
    async (assemblyIds: string[]) => {
      if (!currentTeam) throw new Error('No active team');

      // Deduplicate before comparing counts. `checkComponentUsage` returns one
      // entry per `assembly_components` row and there is no unique constraint on
      // (assembly_id, component_id), so the same assembly can legitimately appear
      // twice. PostgREST collapses `.in()`, so the read-back would come up short
      // and report a failure on a write that succeeded.
      const ids = Array.from(new Set(assemblyIds));
      if (ids.length === 0) return;

      const { data, error: updateError } = await supabase
        .from('assemblies')
        .update({ is_complete: false })
        .in('id', ids)
        .eq('team_id', currentTeam.id)
        .select('id');

      if (updateError) throw updateError;

      if (!data || data.length < ids.length) {
        throw new Error(
          `Marked ${data?.length ?? 0} of ${ids.length} assemblies incomplete — the rest were blocked by row-level security or no longer exist`
        );
      }
    },
    [currentTeam]
  );

  /**
   * Refresh assemblies manually
   */
  const refreshAssemblies = useCallback(async () => {
    await fetchAssemblies();
  }, [fetchAssemblies]);

  // Fetch assemblies on mount and when team changes
  useEffect(() => {
    fetchAssemblies();
  }, [fetchAssemblies]);

  return {
    assemblies,
    loading,
    error,
    addAssembly,
    updateAssembly,
    deleteAssembly,
    checkComponentUsage,
    markAssembliesIncomplete,
    refreshAssemblies,
  };
}
